/**
 * Frontend logging utility for VideBits
 *
 * Sends structured logs to the backend which forwards them to Loki
 * for centralized logging and analysis in Grafana.
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const LOG_ENDPOINT = `${API_URL}/api/logs`;

// Log levels
export const LogLevel = {
  DEBUG: 'debug',
  INFO: 'info',
  WARN: 'warn',
  ERROR: 'error'
};

// Batch configuration
const BATCH_SIZE = 10;
const BATCH_TIMEOUT_MS = 5000;
const MAX_RETRIES = 2;

class Logger {
  constructor() {
    this.logQueue = [];
    this.batchTimer = null;
    this.enabled = true;
    this.minLevel = this._getMinLogLevel();
  }

  /**
   * Determine minimum log level from environment or default to INFO
   */
  _getMinLogLevel() {
    const envLevel = process.env.REACT_APP_LOG_LEVEL?.toLowerCase();

    if (process.env.NODE_ENV === 'development') {
      return envLevel || LogLevel.DEBUG;
    }

    return envLevel || LogLevel.INFO;
  }

  /**
   * Check if a log level should be logged based on minimum level
   */
  _shouldLog(level) {
    const levels = [LogLevel.DEBUG, LogLevel.INFO, LogLevel.WARN, LogLevel.ERROR];
    const minIndex = levels.indexOf(this.minLevel);
    const currentIndex = levels.indexOf(level);

    return currentIndex >= minIndex;
  }

  /**
   * Core logging method
   */
  async _log(level, message, context = {}) {
    if (!this.enabled || !this._shouldLog(level)) {
      return;
    }

    const logEntry = {
      level,
      message: String(message),
      context,
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      url: window.location.href
    };

    // Add to queue
    this.logQueue.push(logEntry);

    // Also log to console for development
    if (process.env.NODE_ENV === 'development') {
      const consoleMethod = level === LogLevel.ERROR ? 'error' :
                           level === LogLevel.WARN ? 'warn' :
                           level === LogLevel.DEBUG ? 'debug' : 'log';
      console[consoleMethod](`[${level.toUpperCase()}]`, message, context);
    }

    // Flush immediately for errors, otherwise batch
    if (level === LogLevel.ERROR || this.logQueue.length >= BATCH_SIZE) {
      await this.flush();
    } else {
      this._scheduleBatchFlush();
    }
  }

  /**
   * Schedule a batch flush
   */
  _scheduleBatchFlush() {
    if (this.batchTimer) {
      return;
    }

    this.batchTimer = setTimeout(() => {
      this.flush();
    }, BATCH_TIMEOUT_MS);
  }

  /**
   * Send queued logs to backend
   */
  async flush() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }

    if (this.logQueue.length === 0) {
      return;
    }

    // Take all queued logs
    const logsToSend = [...this.logQueue];
    this.logQueue = [];

    // Send each log (backend expects one log per request)
    for (const logEntry of logsToSend) {
      await this._sendLog(logEntry);
    }
  }

  /**
   * Send a single log entry to the backend
   */
  async _sendLog(logEntry, retryCount = 0) {
    try {
      const response = await fetch(LOG_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(logEntry),
        // Don't block on logging
        keepalive: true
      });

      if (!response.ok && retryCount < MAX_RETRIES) {
        // Retry on failure
        setTimeout(() => this._sendLog(logEntry, retryCount + 1), 1000 * (retryCount + 1));
      }
    } catch (error) {
      // Silently fail - don't want logging to break the app
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to send log:', error);
      }
    }
  }

  /**
   * Public API methods
   */
  debug(message, context = {}) {
    return this._log(LogLevel.DEBUG, message, context);
  }

  info(message, context = {}) {
    return this._log(LogLevel.INFO, message, context);
  }

  warn(message, context = {}) {
    return this._log(LogLevel.WARN, message, context);
  }

  error(message, context = {}) {
    return this._log(LogLevel.ERROR, message, context);
  }

  /**
   * Enable/disable logging
   */
  setEnabled(enabled) {
    this.enabled = enabled;
  }

  /**
   * Set minimum log level
   */
  setMinLevel(level) {
    this.minLevel = level;
  }
}

// Create singleton instance
const logger = new Logger();

// Flush logs before page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    logger.flush();
  });

  // Also flush on visibility change (mobile browsers)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      logger.flush();
    }
  });
}

export default logger;
